import { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSelector } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'

import { useLocales } from 'locales/hooks'
import { getApplicationSystemInfo, tagManager } from 'shared/utils'
import { SitemodelContext, useSitemodelContent } from 'sitemodel/shared/hooks'
import { ApplicationSystem } from 'sitemodel/shared/hooks/useSitemodelLocalParams/useSitemodelLocalParams'
import { Renderer } from './Renderer'
import useHomeSitemodels from './shared/hooks/useHomeSitemodels'
import useSitemodelFromUrl from './shared/hooks/useSitemodelFromUrl'

type LoadProps = {
  isIndex?: boolean
}

const Load = ({ isIndex }: LoadProps) => {
  const [sitemodelContentFromParent, setSitemodelContentFromParent] =
    useState(null)

  useEffect(() => {
    const onReceivedMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SITEMODEL_CONTENT') {
        const { payload } = event.data
        setSitemodelContentFromParent(payload)
      }
    }

    window.addEventListener('message', onReceivedMessage)
    return () => window.removeEventListener('message', onReceivedMessage)
  }, [])

  const { sitemodel, isFetched } = useSitemodelFromUrl({
    enabled: !sitemodelContentFromParent
  })

  // if pathname is '/' fetch home sitemodels
  const { homeSitemodels } = useHomeSitemodels()

  const location = useLocation()

  const { getSitemodelByLocale } = useLocales()
  const accessRestrictionSitemodelUuid = getSitemodelByLocale({
    demand: 'accessRestrictionSitemodelUuid'
  })

  const currentSitemodel = isIndex ? homeSitemodels?.[0] : sitemodel

  const rawContent =
    sitemodelContentFromParent ?? currentSitemodel?.sitemodel_content

  const content = useSitemodelContent({
    sitemodelContent: rawContent
  })

  const documentTitle = content?.title || sitemodel?.des

  const username = useSelector(({ authentication }) => authentication?.username)

  tagManager.setDataLayer({
    event: 'pageview',
    page: {
      url: location.pathname,
      title: documentTitle
    },
    user: {
      username
    }
  })

  const applicationSystem = useMemo<Partial<ApplicationSystem>>(() => {
    const { ownerUuid } = getApplicationSystemInfo()
    return { owner: ownerUuid || '' }
  }, [])

  const cloudfrontUrl = window.cloudfrontUrl

  if (!content) {
    return null
  }

  if (isFetched && !content?.service) {
    return (
      <Navigate
        to={`/${accessRestrictionSitemodelUuid}`}
        replace
        state={{
          from: location
        }}
      />
    )
  }

  return (
    <>
      <Helmet>
        <title>{documentTitle}</title>
        {applicationSystem?.owner && (
          <link
            rel='icon'
            href={`${cloudfrontUrl}/static-files/favicon/${applicationSystem.owner}.ico`}
          />
        )}
        {applicationSystem?.owner && (
          <link
            rel='manifest'
            href={`${cloudfrontUrl}/static-files/pwa/${applicationSystem.owner}/manifest.json`}
            crossOrigin='anonymous'
          />
        )}
      </Helmet>

      <SitemodelContext.Provider value={content}>
        <Renderer {...content} key={currentSitemodel?.uuid} />
      </SitemodelContext.Provider>
    </>
  )
}

export default Load
